<!-- eslint-disable -->
<template>
  <div class="pa-4">
    <!-- Actions -->
    <div class="d-flex justify-end mb-4">
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon start>mdi-plus</v-icon>
        {{ $t('admin.createFaq') || 'Opret FAQ' }}
      </v-btn>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <!-- FAQ List -->
    <div v-else-if="faqs.length > 0">
      <!-- Group by category -->
      <div v-for="category in faqCategories" :key="category" class="mb-6">
        <div class="d-flex align-center mb-2">
          <v-icon class="mr-2" color="primary">mdi-folder-outline</v-icon>
          <h3 class="text-h6">{{ category || ($t('admin.faqUncategorized') || 'Uden kategori') }}</h3>
          <v-chip size="small" class="ml-2">{{ faqsByCategory(category).length }}</v-chip>
        </div>

        <v-card elevation="1">
          <v-list>
            <template v-for="(faq, index) in faqsByCategory(category)" :key="faq.faqId">
              <v-list-item>
                <template v-slot:prepend>
                  <div class="d-flex flex-column mr-2">
                    <v-btn
                      icon
                      variant="text"
                      size="x-small"
                      :disabled="index === 0"
                      @click="moveFaq(faq, 'up', category)"
                    >
                      <v-icon size="small">mdi-chevron-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      size="x-small"
                      :disabled="index === faqsByCategory(category).length - 1"
                      @click="moveFaq(faq, 'down', category)"
                    >
                      <v-icon size="small">mdi-chevron-down</v-icon>
                    </v-btn>
                  </div>
                </template>

                <v-list-item-title class="font-weight-medium">
                  {{ faq.question }}
                </v-list-item-title>
                <v-list-item-subtitle class="d-flex align-center mt-1" style="gap: 0.5rem;">
                  <v-chip
                    size="x-small"
                    :color="faq.locale === 'da' ? 'primary' : 'info'"
                  >
                    {{ faq.locale?.toUpperCase() || 'DA' }}
                  </v-chip>
                  <v-chip
                    size="x-small"
                    :color="faq.isPublished ? 'success' : 'grey'"
                  >
                    {{ faq.isPublished ? ($t('admin.published') || 'Udgivet') : ($t('admin.draft') || 'Kladde') }}
                  </v-chip>
                  <span class="text-caption text-grey">
                    #{{ faq.sortOrder }}
                  </span>
                </v-list-item-subtitle>

                <template v-slot:append>
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    @click="togglePreview(faq.faqId)"
                  >
                    <v-icon>{{ expandedFaqs.has(faq.faqId) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    @click="editFaq(faq)"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    @click="confirmDelete(faq)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-list-item>

              <!-- Expandable answer preview -->
              <v-expand-transition>
                <div v-show="expandedFaqs.has(faq.faqId)">
                  <div class="px-4 pb-4 ml-12">
                    <v-card variant="tonal" color="grey-lighten-4" class="pa-3">
                      <div class="faq-preview" v-html="faq.answer"></div>
                    </v-card>
                  </div>
                </div>
              </v-expand-transition>

              <v-divider v-if="index < faqsByCategory(category).length - 1"></v-divider>
            </template>
          </v-list>
        </v-card>
      </div>
    </div>

    <!-- Empty State -->
    <v-card v-else elevation="1" class="text-center pa-8">
      <v-icon size="64" color="grey" class="mb-4">mdi-frequently-asked-questions</v-icon>
      <h3 class="text-h6 mb-2">{{ $t('admin.noFaqs') || 'Ingen FAQ endnu' }}</h3>
      <p class="text-grey">{{ $t('admin.noFaqsHint') || 'Opret dit første FAQ-spørgsmål ovenfor.' }}</p>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>
          {{ editingFaq ? ($t('admin.editFaq') || 'Rediger FAQ') : ($t('admin.createFaq') || 'Opret FAQ') }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-combobox
                v-model="faqForm.category"
                :items="existingCategories"
                :label="$t('admin.faqCategory') || 'Kategori'"
                variant="outlined"
                :hint="$t('admin.faqCategoryHint') || 'Select existing or type new'"
                persistent-hint
                clearable
              ></v-combobox>
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="faqForm.locale"
                :items="localeOptions"
                :label="$t('admin.locale') || 'Sprog'"
                variant="outlined"
              ></v-select>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model.number="faqForm.sortOrder"
                :label="$t('admin.sortOrder') || 'Rækkefølge'"
                variant="outlined"
                type="number"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-text-field
            v-model="faqForm.question"
            :label="$t('admin.faqQuestion') || 'Spørgsmål'"
            variant="outlined"
            class="mb-4"
          ></v-text-field>

          <!-- Rich Text Editor for answer -->
          <div class="editor-wrapper mb-4">
            <label class="text-caption text-grey mb-1 d-block">{{ $t('admin.faqAnswer') || 'Svar' }}</label>
            <div class="editor-toolbar">
              <v-btn-group density="compact" variant="outlined">
                <v-btn size="small" @click="editor?.chain().focus().toggleBold().run()" :class="{ 'bg-primary': editor?.isActive('bold') }">
                  <v-icon size="small">mdi-format-bold</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().toggleItalic().run()" :class="{ 'bg-primary': editor?.isActive('italic') }">
                  <v-icon size="small">mdi-format-italic</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().toggleUnderline().run()" :class="{ 'bg-primary': editor?.isActive('underline') }">
                  <v-icon size="small">mdi-format-underline</v-icon>
                </v-btn>
              </v-btn-group>

              <v-btn-group density="compact" variant="outlined" class="ml-2">
                <v-btn size="small" @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()" :class="{ 'bg-primary': editor?.isActive('heading', { level: 3 }) }">
                  <v-icon size="small">mdi-format-header-3</v-icon>
                </v-btn>
              </v-btn-group>

              <v-btn-group density="compact" variant="outlined" class="ml-2">
                <v-btn size="small" @click="editor?.chain().focus().toggleBulletList().run()" :class="{ 'bg-primary': editor?.isActive('bulletList') }">
                  <v-icon size="small">mdi-format-list-bulleted</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().toggleOrderedList().run()" :class="{ 'bg-primary': editor?.isActive('orderedList') }">
                  <v-icon size="small">mdi-format-list-numbered</v-icon>
                </v-btn>
              </v-btn-group>

              <v-btn-group density="compact" variant="outlined" class="ml-2">
                <v-btn size="small" @click="setLink">
                  <v-icon size="small">mdi-link</v-icon>
                </v-btn>
                <v-btn size="small" @click="editor?.chain().focus().unsetLink().run()" :disabled="!editor?.isActive('link')">
                  <v-icon size="small">mdi-link-off</v-icon>
                </v-btn>
              </v-btn-group>
            </div>
            <editor-content :editor="editor" class="editor-content" />
          </div>

          <v-switch
            v-model="faqForm.isPublished"
            :label="$t('admin.published') || 'Udgivet'"
            color="primary"
          ></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveFaq" :loading="saving">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-error">
          {{ $t('admin.deleteFaq') || 'Slet FAQ' }}
        </v-card-title>
        <v-card-text>
          {{ $t('admin.deleteFaqConfirm') || 'Er du sikker på at du vil slette dette spørgsmål?' }}
          <div class="mt-2 font-weight-medium">"{{ faqToDelete?.question }}"</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="deleteFaq" :loading="deleting">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import api from '../../services/api'

const { t } = useI18n()

const faqs = ref([])
const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)

const showDialog = ref(false)
const editingFaq = ref(null)
const faqForm = reactive({
  question: '',
  answer: '',
  category: '',
  locale: 'da',
  sortOrder: 0,
  isPublished: true
})

const showDeleteDialog = ref(false)
const faqToDelete = ref(null)

const expandedFaqs = ref(new Set())

const localeOptions = [
  { value: 'da', title: 'Dansk (DA)' },
  { value: 'en', title: 'English (EN)' }
]

// Computed: unique categories from existing FAQs
const existingCategories = computed(() => {
  const cats = faqs.value
    .map(f => f.category)
    .filter(c => c && c.trim())
  return [...new Set(cats)].sort()
})

// Computed: all categories including uncategorized
const faqCategories = computed(() => {
  const cats = new Set()
  faqs.value.forEach(f => {
    cats.add(f.category || '')
  })
  // Sort: named categories first, then uncategorized
  return [...cats].sort((a, b) => {
    if (!a) return 1
    if (!b) return -1
    return a.localeCompare(b)
  })
})

const faqsByCategory = (category) => {
  return faqs.value
    .filter(f => (f.category || '') === category)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
}

// TipTap Editor
const editor = useEditor({
  extensions: [
    StarterKit,
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    })
  ],
  content: '',
  onUpdate: ({ editor }) => {
    faqForm.answer = editor.getHTML()
  }
})

const setLink = () => {
  const previousUrl = editor.value?.getAttributes('link').href
  const url = window.prompt('URL', previousUrl)

  if (url === null) return

  if (url === '') {
    editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }

  editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

// Watch dialog open to set editor content
watch(showDialog, (isOpen) => {
  if (isOpen && editor.value) {
    editor.value.commands.setContent(faqForm.answer || '')
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

const togglePreview = (faqId) => {
  const newSet = new Set(expandedFaqs.value)
  if (newSet.has(faqId)) {
    newSet.delete(faqId)
  } else {
    newSet.add(faqId)
  }
  expandedFaqs.value = newSet
}

// --- API calls ---
const loadFaqs = async () => {
  loading.value = true
  try {
    const response = await api.get('/admin/faqs')
    faqs.value = response.data.faqs || []
  } catch (error) {
    console.error('Failed to load FAQs:', error)
  } finally {
    loading.value = false
  }
}

const openCreateDialog = () => {
  editingFaq.value = null
  // Set next sort order
  const maxOrder = faqs.value.reduce((max, f) => Math.max(max, f.sortOrder || 0), 0)
  Object.assign(faqForm, {
    question: '',
    answer: '',
    category: '',
    locale: 'da',
    sortOrder: maxOrder + 1,
    isPublished: true
  })
  showDialog.value = true
}

const editFaq = (faq) => {
  editingFaq.value = faq
  Object.assign(faqForm, {
    question: faq.question,
    answer: faq.answer,
    category: faq.category || '',
    locale: faq.locale || 'da',
    sortOrder: faq.sortOrder || 0,
    isPublished: !!faq.isPublished
  })
  showDialog.value = true
}

const closeDialog = () => {
  showDialog.value = false
  editingFaq.value = null
}

const saveFaq = async () => {
  if (!faqForm.question.trim()) {
    alert(t('admin.faqQuestionRequired') || 'Spørgsmål er påkrævet')
    return
  }

  saving.value = true
  try {
    if (editingFaq.value) {
      await api.put('/admin/faqs', {
        faqId: editingFaq.value.faqId,
        question: faqForm.question,
        answer: faqForm.answer,
        category: faqForm.category || null,
        locale: faqForm.locale,
        sortOrder: faqForm.sortOrder,
        isPublished: faqForm.isPublished
      })
    } else {
      await api.post('/admin/faqs', {
        question: faqForm.question,
        answer: faqForm.answer,
        category: faqForm.category || null,
        locale: faqForm.locale,
        sortOrder: faqForm.sortOrder,
        isPublished: faqForm.isPublished
      })
    }

    closeDialog()
    loadFaqs()
  } catch (error) {
    console.error('Failed to save FAQ:', error)
    alert(error.response?.data?.error || 'Failed to save')
  } finally {
    saving.value = false
  }
}

const confirmDelete = (faq) => {
  faqToDelete.value = faq
  showDeleteDialog.value = true
}

const deleteFaq = async () => {
  if (!faqToDelete.value) return

  deleting.value = true
  try {
    await api.delete('/admin/faqs', {
      data: { faqId: faqToDelete.value.faqId }
    })

    faqs.value = faqs.value.filter(f => f.faqId !== faqToDelete.value.faqId)
    showDeleteDialog.value = false
    faqToDelete.value = null
  } catch (error) {
    console.error('Failed to delete FAQ:', error)
    alert(error.response?.data?.error || 'Failed to delete')
  } finally {
    deleting.value = false
  }
}

const moveFaq = async (faq, direction, category) => {
  const categoryFaqs = faqsByCategory(category)
  const currentIndex = categoryFaqs.findIndex(f => f.faqId === faq.faqId)

  if (
    (direction === 'up' && currentIndex === 0) ||
    (direction === 'down' && currentIndex === categoryFaqs.length - 1)
  ) return

  const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
  const swapFaq = categoryFaqs[swapIndex]

  // Swap sort orders
  const tempOrder = faq.sortOrder
  faq.sortOrder = swapFaq.sortOrder
  swapFaq.sortOrder = tempOrder

  // Persist both
  try {
    await Promise.all([
      api.put('/admin/faqs', { faqId: faq.faqId, sortOrder: faq.sortOrder }),
      api.put('/admin/faqs', { faqId: swapFaq.faqId, sortOrder: swapFaq.sortOrder })
    ])
    // Trigger reactivity
    faqs.value = [...faqs.value]
  } catch (error) {
    console.error('Failed to reorder FAQ:', error)
    // Revert on failure
    loadFaqs()
  }
}

onMounted(() => {
  loadFaqs()
})
</script>

<style scoped>
.editor-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  overflow: hidden;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  background: #f5f5f5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.editor-content {
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
}

.editor-content :deep(.ProseMirror) {
  min-height: 168px;
  outline: none;
}

.editor-content :deep(.ProseMirror p) {
  margin-bottom: 0.75em;
}

.editor-content :deep(.ProseMirror h3) {
  font-size: 1.25em;
  font-weight: bold;
  margin-top: 1em;
  margin-bottom: 0.5em;
}

.editor-content :deep(.ProseMirror ul),
.editor-content :deep(.ProseMirror ol) {
  padding-left: 1.5em;
  margin-bottom: 0.75em;
}

.editor-content :deep(.ProseMirror a) {
  color: #1976d2;
  text-decoration: underline;
}

.faq-preview :deep(p) {
  margin-bottom: 0.5em;
}

.faq-preview :deep(a) {
  color: #1976d2;
  text-decoration: underline;
}

.faq-preview :deep(ul),
.faq-preview :deep(ol) {
  padding-left: 1.5em;
}
</style>